import cx from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import styles from './ai-assistant.module.css';
import { Icon } from 'assets/icons';
import { useAIChatService } from 'hooks/ai/chat';
import { toast } from 'react-toastify';
import { AIChatBubble } from './bubble';

export const AIAssistantArea = () => {
  const { sendChat } = useAIChatService();

  const [chat_history, setChatHistory] = useState([]);
  const [is_thinking, setIsThinking] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [show_scroll_btn, setShowScrollBtn] = useState(false);

  const chat_end_ref = useRef(null);
  const chat_display_ref = useRef(null);

  const scrollToBottom = (behavior = 'smooth') => {
    chat_end_ref.current?.scrollIntoView({ behavior });
  };

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    scrollToBottom();
  }, [chat_history]);

  // Show/hide the scroll-to-bottom button based on scroll position
  const handleScroll = () => {
    const el = chat_display_ref.current;
    if (!el) return;
    const distance_from_bottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowScrollBtn(distance_from_bottom > 80);
  };

  const submit = async () => {
    if (is_thinking || !prompt.trim()) return;

    setIsThinking(() => true);
    setPrompt('');
    try {
      const user_message = { role: 'user', content: prompt };
      setChatHistory((prev) => [
        ...prev,
        user_message,
        { role: 'assistant', type: 'thinking', content: 'Processing your request...' }
      ]);

      //
      const data = {
        messages: [...chat_history, user_message],
        subscription: 'enterprise'
      };

      const response = await sendChat({ data });
      if (!response) {
        toast.error('No response from the assistant. Please try again.');
      } else {
        const { reply } = response;
        setChatHistory((prev) => [...prev.slice(0, -1), { role: 'assistant', content: reply }]);
      }
    } finally {
      setIsThinking(() => false);
      setChatHistory((prev) => prev.filter((msg) => msg.type !== 'thinking'));
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.chatDisplayWrapper}>
        <div ref={chat_display_ref} className={styles.chatDisplay} onScroll={handleScroll}>
          {chat_history.map((chat, index) => (
            <AIChatBubble
              key={index}
              message={chat.content}
              type={chat.type}
              is_user={chat.role === 'user'}
            />
          ))}
          <div ref={chat_end_ref} />
        </div>
        {show_scroll_btn && (
          <button
            className={styles.scrollToBottomBtn}
            onClick={() => scrollToBottom()}
            aria-label="Scroll to latest message"
          >
            <Icon name="arrow_down" width={16} height={16} />
          </button>
        )}
      </div>
      <div className={styles.inputArea}>
        <textarea
          className={styles.textInput}
          type="text"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="Ask me anything…"
          rows={1}
        />
        <div className={styles.actions}>
          <span className={styles.counterweight} />
          <span
            className={cx(styles.sendIcon, { [styles.disabled]: is_thinking || !prompt.trim() })}
            onClick={submit}
          >
            <Icon name="send" width={16} height={16} />
          </span>
        </div>
      </div>
    </div>
  );
};
